import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import AppHeader from "./AppHeader";
import Sidebar from "./Sidebar";
import ActivityMenu from "./ActivityMenu";
import { JobsProvider } from "../contexts/JobsProvider";
// import DocumentUpload from "./DocumentUpload";
// import DocumentList from "./DocumentList";
import QAChat from "./QAChat";
import ProductCatalogAgent from "./ProductCatalogAgent";
import LVAgent from "./LVAgent";
import MatchingAgent from "./MatchingAgent";
import API_BASE_URL from "../config";
import { cursorParams } from "../utils/listCursor";
import SearchPage from "./SearchPage";
import Workflow from "./Workflow";
import Knowdelgebase from "./Knowdelgebase";
import Analyticalpage from "./Analyticalpage";
import AdminUsers from "./AdminUsers";
import FeedbackReview from "./FeedbackReview";
import LoadTestPage from "./LoadTestPage";
import AuditLog from "./AuditLog";
import AdminCollections from "./AdminCollections";
import AdminConnectors from "./AdminConnectors";
import AgentsTab from "./AgentsTab";
import { appContentClass } from "../utils/contentSurface";
import { MOBILE_QUERY, isMobileViewport } from "../utils/breakpoints";

// Namespaced per workspace — a thread_id created under one workspace must
// never be reused under another (2026-09-04: was the root cause of a
// cross-workspace hallucination regression, since a reused thread's history
// gets fed to _resolve_history_node regardless of which workspace it
// actually belonged to). Still used so a plain page reload resumes the
// right workspace's own thread (see the useState initializer below) —
// switching workspaces at runtime, however, always starts a new chat rather
// than resuming this key's value (see the effect below).
const threadIdKey = (db) => `qa_thread_id:${db || "default"}`;

export default function Dashboard({ user, onSignOut }) {
  const [activeTab, setActiveTab] = useState("chat");
  const [refreshKey, setRefreshKey] = useState(0);
  const [runsRefreshKey, setRunsRefreshKey] = useState(0);
  const [openedRunId, setOpenedRunId] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [openTakeoffJob, setOpenTakeoffJob] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [discoverDocId, setDiscoverDocId] = useState("");
  // Phones start with the drawer closed; desktops with the sidebar expanded.
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDb = searchParams.get("db") ?? "Lucking";

  const [threadId, setThreadId] = useState(
    () => localStorage.getItem(threadIdKey(selectedDb)) || null,
  );
  const [conversations, setConversations] = useState([]);
  // X-Next-Cursor of the conversation list: null = everything is loaded.
  const [conversationsCursor, setConversationsCursor] = useState(null);
  const [searchType, setSearchType] = useState(null);
  const [searchCategory, setSearchCategory] = useState(null);
  const [pendingSearchQuery, setPendingSearchQuery] = useState(null);
  const [searchSeed, setSearchSeed] = useState(null);
  // Stable identity so QAChat's seeding effect doesn't re-run on every render.
  const clearSearchSeed = useCallback(() => setSearchSeed(null), []);

  const setSelectedDb = (db) =>
    setSearchParams(
      (prev) => {
        prev.set("db", db);
        return prev;
      },
      { replace: true },
    );

  const handleUploadComplete = () => setRefreshKey((prev) => prev + 1);

  // Open a background job's result in the workflow area. Failed jobs have no
  // result to fetch — render them directly so the error + Retry show immediately.
  const handleOpenJob = useCallback(async (job) => {
    setOpenedRunId(null);
    setActiveTab("workflow");
    // Takeoff lives in the SSE subsystem (no /api/v1/jobs/{id}) — route into the
    // Takeoff workflow and let it reattach to the run by job_id.
    if (job.agent_type === "takeoff") {
      setOpenJob(null);
      setDiscoverDocId("");
      setSelectedWorkflowId("WF-03");
      setOpenTakeoffJob(job);
      return;
    }
    // Running → live progress panel; failed → error + Retry (both via openJob).
    if (job.status === "running" || job.status === "failed") {
      setDiscoverDocId("");
      setOpenJob({ ...job });
      return;
    }
    // A completed schema job has no report — deep-link into the Discovery view for
    // that document (params hold the document_id; the list row doesn't, so fetch it).
    if (job.agent_type === "schema") {
      setOpenJob(null);
      let docId = job.params?.document_id;
      if (!docId) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/v1/jobs/${job.id}`);
          if (res.ok) docId = (await res.json()).params?.document_id;
        } catch { /* ignore */ }
      }
      if (docId) {
        setSelectedWorkflowId("WF-01");   // renders the Z-document / discovery workflow
        setDiscoverDocId(docId);
      }
      return;
    }
    if (job.status === "failed") {
      setOpenJob({ ...job });
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/jobs/${job.id}`);
      if (res.ok) setOpenJob(await res.json());
    } catch {
      // transient — leave the workflow grid in place
    }
  }, []);

  // Re-dispatch a failed job, then close the panel (sidebar polling shows the new job).
  const handleRetryJob = useCallback(async () => {
    if (!openJob) return;
    try {
      await fetch(`${API_BASE_URL}/api/v1/jobs/${openJob.id}/retry`, { method: "POST" });
    } catch {
      // ignore — sidebar polling reflects the outcome
    }
    setOpenJob(null);
  }, [openJob]);

  const fetchConversationPage = useCallback(async (after) => {
    const params = new URLSearchParams({ database: selectedDb });
    if (after) after.forEach((value, key) => params.set(key, value));
    const res = await fetch(`${API_BASE_URL}/api/v1/conversations?${params}`, {
      headers: new Headers({
        "ngrok-skip-browser-warning": "69420",
      }),
    });
    if (!res.ok) return null;
    return { items: await res.json(), next: res.headers.get("X-Next-Cursor") };
  }, [selectedDb]);

  const loadConversations = useCallback(async () => {
    try {
      const page = await fetchConversationPage(null);
      if (page) {
        setConversations(page.items);
        setConversationsCursor(page.next);
      }
    } catch {
      // sidebar is optional — degrade silently
    }
  }, [fetchConversationPage]);

  const loadMoreConversations = useCallback(async () => {
    const after = cursorParams(conversationsCursor);
    if (!after) return;
    try {
      const page = await fetchConversationPage(after);
      if (page) {
        setConversations((prev) => {
          const seen = new Set(prev.map((c) => c.thread_id));
          return [...prev, ...page.items.filter((c) => !seen.has(c.thread_id))];
        });
        setConversationsCursor(page.next);
      }
    } catch {
      // sidebar is optional — degrade silently
    }
  }, [conversationsCursor, fetchConversationPage]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Switching the workspace dropdown always starts a fresh chat — a
  // workspace switch reads as "new context", not "maybe an old conversation
  // from last time reappears". Skips the very first run so a plain page
  // reload (not a switch) still resumes normally via the useState
  // initializer above; only a real subsequent change to selectedDb resets.
  const isInitialDbRender = useRef(true);
  useEffect(() => {
    if (isInitialDbRender.current) {
      isInitialDbRender.current = false;
      return;
    }
    setThreadId(null);
  }, [selectedDb]);

  // Auto-close sidebar on mobile
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => {
      if (e.matches) setSidebarOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const handleNewChat = useCallback(() => {
    setThreadId(null);
    localStorage.removeItem(threadIdKey(selectedDb));
    setActiveTab("chat");
  }, [selectedDb]);

  // The chat found the remembered conversation gone / not ours: forget it (stay
  // on the current tab; only an id still in use is dropped).
  const handleThreadUnavailable = useCallback((id) => {
    setThreadId((current) => (current === id ? null : current));
    if (localStorage.getItem(threadIdKey(selectedDb)) === id) {
      localStorage.removeItem(threadIdKey(selectedDb));
    }
  }, [selectedDb]);

  const handleSelectConversation = useCallback((id) => {
    if (isMobileViewport()) setSidebarOpen(false);
    setThreadId(id);
    localStorage.setItem(threadIdKey(selectedDb), id);
    setActiveTab("chat");
  }, [selectedDb]);

  const handleRenameConversation = useCallback(async (id, title) => {
    try {
      await fetch(`${API_BASE_URL}/api/v1/conversations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      setConversations((prev) =>
        prev.map((c) => (c.thread_id === id ? { ...c, title } : c)),
      );
    } catch {
      // ignore
    }
  }, []);

  const handleOpenRun = useCallback((runId) => {
    setOpenJob(null);
    setOpenedRunId(runId);
    setActiveTab("workflow");
  }, []);

  // Stable identity so the Takeoff reattach effect fires once per opened job.
  const handleTakeoffReattached = useCallback(() => setOpenTakeoffJob(null), []);

  const handleDeleteConversation = useCallback(
    async (id) => {
      try {
        await fetch(`${API_BASE_URL}/api/v1/conversations/${id}`, {
          method: "DELETE",
        });
        setConversations((prev) => prev.filter((c) => c.thread_id !== id));
        if (id === threadId) {
          setThreadId(null);
          localStorage.removeItem(threadIdKey(selectedDb));
        }
      } catch {
        // ignore
      }
    },
    [threadId, selectedDb],
  );

  return (
    <JobsProvider>
    <div className="app">
      <div className="layout">
        {/* Mobile overlay — clicking it closes the sidebar */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sidebarOpen={sidebarOpen}
          conversations={conversations}
          hasMoreConversations={Boolean(cursorParams(conversationsCursor))}
          onLoadMoreConversations={loadMoreConversations}
          threadId={threadId}
          onNewChat={handleNewChat}
          onSelectConversation={handleSelectConversation}
          onRenameConversation={handleRenameConversation}
          onDeleteConversation={handleDeleteConversation}
          toggleSidebar={() => setSidebarOpen((v) => !v)}
          user={user}
          onSignOut={onSignOut}
          selectedDb={selectedDb}
          onDbChange={setSelectedDb}
          runsRefreshKey={runsRefreshKey}
          onOpenRun={handleOpenRun}
          searchCategory={searchCategory}
          onSearchCategoryChange={(id) => {
            setSearchCategory(id);
            setActiveTab("search");
          }}
          onSelectRecentSearch={(q) => {
            setPendingSearchQuery(q);
            setActiveTab("search");
          }}
          onOpenWorkflow={(wfId) => {
            // Selecting a workflow from the sidebar opens the Agentic Workflows
            // grid (clearing any open saved run/job) and expands that workflow.
            setOpenJob(null);
            setOpenedRunId(null);
            setSelectedWorkflowId(wfId);
            setActiveTab("workflow");
          }}
        />

        <div className="app-main-column">
          {/* Header with the global activity area — visible on every tab. */}
          <AppHeader
            activeTab={activeTab}
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen((v) => !v)}
          >
            <ActivityMenu
              onOpenJob={handleOpenJob}
              onOpenRun={handleOpenRun}
              refreshKey={runsRefreshKey}
            />
          </AppHeader>
        <main className={appContentClass(activeTab)}>
          {activeTab === "chat" && (
            <QAChat
              database={selectedDb}
              threadId={threadId}
              initialQuestion={searchSeed?.question}
              onSeedConsumed={clearSearchSeed}
              onThreadCreated={(id) => {
                setThreadId(id);
                localStorage.setItem(threadIdKey(selectedDb), id);
                setSearchSeed(null);
              }}
              onThreadUnavailable={handleThreadUnavailable}
              onConversationsRefresh={loadConversations}
            />
          )}

          {activeTab === "search" && (
            <SearchPage
              database={selectedDb}
              activeType={searchType}
              setActiveType={setSearchType}
              category={searchCategory}
              setCategory={setSearchCategory}
              initialQuery={pendingSearchQuery}
              onInitialQueryConsumed={() => setPendingSearchQuery(null)}
              onAskAboutThis={(r) => {
                setSearchSeed({ question: r.snippet || r.title, documentId: r.document_id });
                setActiveTab("chat");
              }}
            />
          )}

          {activeTab === "workflow" && (
            <Workflow
              database={selectedDb}
              onRunApproved={() => setRunsRefreshKey((k) => k + 1)}
              openedRunId={openedRunId}
              onCloseRun={() => setOpenedRunId(null)}
              openJob={openJob}
              onCloseJob={() => setOpenJob(null)}
              onRetryJob={handleRetryJob}
              selectedWorkflowId={selectedWorkflowId}
              discoverDocId={discoverDocId}
              openTakeoffJob={openTakeoffJob}
              onTakeoffReattached={handleTakeoffReattached}
            />
          )}

          {activeTab === "kb" && (
            <Knowdelgebase
              onUploadComplete={handleUploadComplete}
              database={selectedDb}
              refreshKey={refreshKey}
            />
          )}

          {activeTab === "analytics" && <Analyticalpage />}
          {activeTab === "agents" && <AgentsTab database={selectedDb} />}

          {activeTab === "admin" && user?.role === "admin" && <AdminUsers />}

          {activeTab === "feedback" && user?.role === "admin" && <FeedbackReview />}
          {activeTab === "audit" && user?.role === "admin" && <AuditLog />}

          {activeTab === "collections" && user?.role === "admin" && <AdminCollections />}
          {activeTab === "connectors" && user?.role === "admin" && <AdminConnectors />}

          {activeTab === "loadtest" && user?.role === "admin" && <LoadTestPage />}

          {/* {activeTab === "files" && (
            <div className="files-layout">
              <DocumentUpload
                onUploadComplete={handleUploadComplete}
                database={selectedDb}
              />
              <DocumentList refreshKey={refreshKey} database={selectedDb} />
            </div>
          )} */}
        </main>
        </div>
      </div>
    </div>
    </JobsProvider>
  );
}
